import { lengthBetween, notEmail, required } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  type ChangeEvent,
  type FocusEvent,
  useState,
  useImperativeHandle,
  forwardRef,
  type Ref,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { TEALIUM_FINAL_JOB_SUMMARY_CHARACTER_COUNT } from 'src/app/shared/constants/dispatcherId';
import {
  JOB_SUMMARY_INTERACTION,
  JOB_SUMMARY_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { removeEmoji } from 'src/app/utils/emoji';
import { getProductFeatureByType } from 'src/app/utils/getProductFeatureByType';
import {
  type Validators,
  filterContent,
  validateField,
} from 'src/app/utils/validation';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { sendContentSafetyTrackingEvents } from 'src/app/v1/dataAnalytics/utils/sendContentSafetyTrackingEvents';
import { sendPerUnitBillingValidationTrackingEvents } from 'src/app/v1/dataAnalytics/utils/sendPerUnitBillingValidationTrackingEvents';
import dispatchTealium from 'src/app/v1/state/dispatcher/tealium';
import { actionUpdateJobSummary } from 'src/app/v1/state/modules/formData/jobSummary';
import { useEmojiRestrictionsFeature } from 'src/app/v1/views/hooks/useEmojiRestrictionsFeature';
import { Textarea } from 'src/app/v1/views/shared/Textarea';
import type { AppState } from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

import translations from './.vocab';

export const INPUT_MAX_LENGTH = 150;

export const JobSummary = forwardRef((_props, ref: Ref<any>) => {
  const {
    jobSummary,
    jobLocationCountry,
    hirerNation,
    productFeaturesFromRedux,
  } = useSelector(
    (state: AppState) => ({
      jobSummary: state.formData.jobSummary,
      jobLocationCountry: state.session.jobLocationCountry,
      hirerNation: state.session?.hirerAccount?.nation,
      productFeaturesFromRedux: state.session.productFeatures,
    }),
    shallowEqual,
  );
  const country = (jobLocationCountry ?? hirerNation ?? 'AU').toLowerCase();
  const { t } = useTranslations(translations);
  const { getLanguage } = useConfig();

  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );
  const [inputValue, setInputValue] = useState<string>(jobSummary);

  const dispatch = useDispatch();

  const { cartFeatures } = useShoppingCart();

  const doNewFeaturesHavePerUnitBilling = Boolean(
    getProductFeatureByType({
      featureType: 'PerUnitBilling',
      productFeatures: cartFeatures,
    }),
  );
  const doExistingFeaturesHavePerUnitBilling = Boolean(
    getProductFeatureByType({
      featureType: 'PerUnitBilling',
      productFeatures: productFeaturesFromRedux,
    }),
  );
  const isPerUnitBilling =
    doNewFeaturesHavePerUnitBilling || doExistingFeaturesHavePerUnitBilling;

  const { shouldRestrictEmojis } = useEmojiRestrictionsFeature();

  useImperativeHandle(ref, () => ({
    handleValidation: (skipIfNull = false) =>
      handleInputValidation(inputValue, skipIfNull),
    inputValue,
  }));

  const handleInputValidation = (
    value: string,
    skipIfNull = false,
  ): string | null => {
    let validators: Validators = [required, lengthBetween, filterContent];
    if (isPerUnitBilling) validators = [...validators, notEmail];

    const validationResult = validateField(
      {
        field: 'jobSummary',
        validators,
        validatorProps: {
          min: 1,
          max: INPUT_MAX_LENGTH,
          requiredLabel: t('job-summary-required-label'),
          country,
          sendTrackingEvents: sendContentSafetyTrackingEvents,
        },
        value,
      },
      skipIfNull,
      getLanguage(),
      [sendPerUnitBillingValidationTrackingEvents],
    );

    setValidationMessage(validationResult);

    return validationResult;
  };

  const handleOnBlur = (event: FocusEvent<HTMLTextAreaElement>) => {
    const eventData: TrackingEvent = {
      eventName: JOB_SUMMARY_PRESSED,
      objectInteraction: JOB_SUMMARY_INTERACTION,
    };
    getTrackingAdapter.sendLinkEvent(eventData);

    const errorMessage = handleInputValidation(event.target.value);

    if (!errorMessage) {
      dispatch(actionUpdateJobSummary(event.target.value));
      dispatch(
        dispatchTealium(
          TEALIUM_FINAL_JOB_SUMMARY_CHARACTER_COUNT,
          event.target.value,
        ),
      );
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const trimmedValue = shouldRestrictEmojis
      ? removeEmoji(event.target.value)
      : event.target.value;

    setInputValue(trimmedValue);
    handleInputValidation(trimmedValue);
  };

  return (
    <Textarea
      id="JobSummaryTextarea"
      hasCounter
      maxLength={INPUT_MAX_LENGTH}
      message={validationMessage}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      valid={!validationMessage}
      value={inputValue}
      lines={3}
    />
  );
});
